import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import PropTypes from "prop-types";

import QuizHeader from "./components/QuizHeader";
import QuizTimer from "./components/QuizTimer";
import QuestionNavigator from "./components/QuestionNavigator";
import QuestionCard from "./components/QuestionCard";
import SubmitQuizButton from "./components/SubmitQuizButton";

import useQuizAttempt from "./hooks/useQuizAttempt";

import { useSubmitQuizAttemptMutation } from "../../slices/quizAttemptApiSlice";

const QuizAttemptScreen = ({ quiz, attempt }) => {
  const navigate = useNavigate();

  const questions = attempt?.questions || [];

  console.log("========================================");
  console.log("QuizAttemptScreen Render");
  console.log("Attempt:", attempt);
  console.log("Questions:", questions);

  const [submitQuizAttempt, { isLoading: isSubmitting }] =
    useSubmitQuizAttemptMutation();

  const {
    currentQuestion,
    currentQuestionIndex,
    answeredQuestions,
    handleAnswerSelect,
    getSelectedAnswer,
    goToQuestion,
    nextQuestion,
    previousQuestion,
    getSubmitPayload,
    clearAttemptStorage,
  } = useQuizAttempt({
    questions,
    attemptId: attempt?._id,
  });

  /**
   * Timer calculation
   */
  const remainingSeconds = useMemo(() => {
    if (!attempt) {
      return 0;
    }

    const durationMinutes =
      attempt.durationMinutes || quiz.durationMinutes || 0;

    const totalSeconds = durationMinutes * 60;

    if (!attempt.startedAt) {
      return totalSeconds;
    }

    const elapsedSeconds = Math.floor(
      (Date.now() - new Date(attempt.startedAt).getTime()) / 1000
    );

    const remaining = Math.max(totalSeconds - elapsedSeconds, 0);

    console.log("Timer Calculation");
    console.log("Duration Minutes:", durationMinutes);
    console.log("Elapsed Seconds:", elapsedSeconds);
    console.log("Remaining Seconds:", remaining);

    return remaining;
  }, [attempt, quiz.durationMinutes]);

  /**
   * Submit Quiz
   */
  const handleSubmitQuiz = async (autoSubmit = false) => {
  console.log("========================================");
  console.log("handleSubmitQuiz()");
  console.log("Time:", new Date().toLocaleTimeString());
  console.log("autoSubmit:", autoSubmit);
  console.log("isSubmitting:", isSubmitting);
  console.log("Attempt object:", attempt);
  console.log("Attempt ID:", attempt?._id);

  // =====================================================
  // 1. Prevent duplicate submission
  // =====================================================

  if (isSubmitting) {
    console.log(
      "Submission blocked - already submitting."
    );
    return;
  }

  // =====================================================
  // 2. Validate attempt ID
  // =====================================================

  const currentAttemptId =
    attempt?._id?.toString?.() || attempt?._id;

  if (!currentAttemptId) {
    console.error(
      "Submission blocked - attempt ID missing.",
      attempt
    );

    alert(
      "Unable to submit quiz because the attempt ID is missing."
    );

    return;
  }

  console.log(
    "Current Attempt ID:",
    currentAttemptId
  );

  // =====================================================
  // 3. Confirmation
  // =====================================================

  if (!autoSubmit) {
    console.log(
      "Showing confirmation dialog..."
    );

    const confirmed = window.confirm(
      "Are you sure you want to submit the quiz?"
    );

    console.log(
      "Confirmation Result:",
      confirmed
    );

    if (!confirmed) {
      console.log(
        "User cancelled submission."
      );

      return;
    }
  }

  try {
    // ===================================================
    // 4. Build payload
    // ===================================================

    const submitPayload = getSubmitPayload();

    const payload = {
      attemptId: currentAttemptId,
      ...submitPayload,
    };

    console.log(
      "========== SUBMIT PAYLOAD =========="
    );

    console.log(
      JSON.stringify(
        payload,
        null,
        2
      )
    );

    // ===================================================
    // 5. Submit
    // ===================================================

    console.log(
      "Calling submitQuizAttempt()..."
    );

    const response =
      await submitQuizAttempt(
        payload
      ).unwrap();

    console.log(
      "========== SUBMIT API SUCCESS =========="
    );

    console.log(
      "Response:",
      response
    );

    // ===================================================
    // 6. Determine returned attempt ID
    //
    // Backend currently returns the complete
    // QuizAttempt document.
    //
    // But support both:
    //
    // response._id
    // response.attemptId
    // currentAttemptId
    // ===================================================

    const submittedAttemptId =
      response?._id?.toString?.() ||
      response?.attemptId?.toString?.() ||
      currentAttemptId;

    console.log(
      "Submitted Attempt ID:",
      submittedAttemptId
    );

    // ===================================================
    // 7. Validate ID before navigation
    // ===================================================

    if (
      !submittedAttemptId ||
      submittedAttemptId === "undefined" ||
      submittedAttemptId === "null"
    ) {
      console.error(
        "❌ Submit succeeded but no attempt ID was available.",
        {
          response,
          currentAttemptId,
        }
      );

      alert(
        "Quiz was submitted, but the result page could not be opened."
      );

      return;
    }

    // ===================================================
    // 8. Clear local storage
    // ===================================================

    console.log(
      "Clearing local storage..."
    );

    clearAttemptStorage();

    // ===================================================
    // 9. Navigate to result page
    // ===================================================

    const resultUrl =
      `/quiz-attempts/${submittedAttemptId}`;

    console.log(
      "Navigating to:",
      resultUrl
    );

    navigate(resultUrl);

    console.log(
      "navigate() executed"
    );

  } catch (error) {
    console.log(
      "========================================"
    );

    console.log(
      "API FAILED"
    );

    console.error(
      error
    );

    if (error?.status) {
      console.log(
        "Status:",
        error.status
      );
    }

    if (error?.data) {
      console.log(
        "Response:",
        error.data
      );
    }
  }
};


  const handleTimeExpire = () => {
    console.log("========================================");
    console.log("Timer expired");
    console.log("Current Time:", new Date().toLocaleTimeString());

    handleSubmitQuiz(true);
  };

  if (!attempt) {
    return (
      <div className="p-5 text-center text-gray-500">
        Loading quiz attempt...
      </div>
    );
  }

  if (!questions.length || !currentQuestion) {
    return (
      <div className="p-5 text-center text-gray-500">
        Loading generated question paper...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto space-y-4">
        <QuizHeader
          quizName={quiz.name}
          attemptNumber={attempt.attemptNumber}
          totalQuestions={questions.length}
          totalMarks={attempt.totalMarks}
          currentQuestion={currentQuestionIndex + 1}
          answeredQuestions={answeredQuestions.length}
        />

        <div className="flex justify-end">
          <QuizTimer
            durationSeconds={remainingSeconds}
            onTimeExpire={handleTimeExpire}
          />
        </div>

        <QuestionNavigator
          totalQuestions={questions.length}
          currentQuestion={currentQuestionIndex + 1}
          answeredQuestions={answeredQuestions}
          onQuestionChange={goToQuestion}
        />

        <QuestionCard
          questionNumber={currentQuestionIndex + 1}
          totalQuestions={questions.length}
          question={currentQuestion}
          selectedAnswer={getSelectedAnswer(currentQuestion._id)}
          onAnswerSelect={handleAnswerSelect}
        />

        <div className="flex justify-between">
          <button
            type="button"
            onClick={previousQuestion}
            disabled={currentQuestionIndex === 0}
            className="px-5 py-2 rounded-lg border disabled:bg-gray-100"
          >
            Previous
          </button>

          {currentQuestionIndex < questions.length - 1 ? (
            <button
              type="button"
              onClick={nextQuestion}
              className="px-5 py-2 rounded-lg bg-blue-600 text-white"
            >
              Next
            </button>
          ) : (
            <SubmitQuizButton
              onSubmit={() => {
                console.log("========================================");
                console.log("Submit button clicked");
                console.log("Time:", new Date().toLocaleTimeString());

                handleSubmitQuiz(false);
              }}
              isSubmitting={isSubmitting}
            />
          )}
        </div>
      </div>
    </div>
  );
};

QuizAttemptScreen.propTypes = {
  quiz: PropTypes.shape({
    name: PropTypes.string.isRequired,
    durationMinutes: PropTypes.number.isRequired,
  }).isRequired,

  attempt: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    attemptNumber: PropTypes.number.isRequired,
    totalMarks: PropTypes.number.isRequired,
    durationMinutes: PropTypes.number,
    startedAt: PropTypes.string,
    questions: PropTypes.array.isRequired,
  }),
};

export default QuizAttemptScreen;